// Conteos centralizados de los badges del Home y dashboards. Todos aplican el corte
// FECHA_CORTE (1-ene-2026): las facturas/movimientos históricos pre-Clay no inflan los
// badges. Las facturas recibidas excluyen NCs (tipo_dte=61): reducen deuda, no son DTEs
// pendientes pagables. La regla vive acá una sola vez — consumidores: Admin Home y
// DashboardFinanciero.
import {
  CLASIFICACION,
  TIPO_DOCUMENTO,
  derivarClasificacion,
  normalizarExterna,
  normalizarHonorario,
  normalizarSiiRecibida,
} from "./compras/documentosCosto";
import { FECHA_CORTE } from "./estadoCuenta/saldoInicial";
import { client } from "./supabase";

// Facturas SII recibidas pendientes desde el corte, excluyendo NCs (tipo_dte=61).
export async function getCountRecibidasPendientes(pais) {
  const { count } = await client
    .from("facturas_sii_recibidas")
    .select("id", { count: "exact", head: true })
    .eq("pais", pais)
    .eq("estado_vinculacion", "pendiente")
    .gte("fecha_emision", FECHA_CORTE)
    .neq("tipo_dte", 61);
  return count || 0;
}

// Facturas de COMPRA SIN CLASIFICAR (eje CONTABLE) — el mismo número que la card "SIN CLASIFICAR"
// de ComprasTab. Reproduce EXACTO su universo: las 3 fuentes (SII recibidas + externas + honorarios
// que computan) con los MISMOS filtros de `useDocumentosCosto` (país, corte FECHA_CORTE en sii/
// honorarios, externas no anuladas), normalizadas con las MISMAS funciones puras y pasadas por el
// MISMO `derivarClasificacion`; se excluyen las NC (no se clasifican). Se reusan las funciones de
// `documentosCosto` a propósito: un solo criterio, sin riesgo de divergir del número de la card.
export async function getCountComprasSinClasificar(pais) {
  const [sii, externas, honorarios] = await Promise.all([
    client
      .from("facturas_sii_recibidas")
      .select("id, tipo_dte, estado_vinculacion, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("facturas_externas").select("id, estado, cuenta_er").eq("pais", pais).neq("estado", "anulada"),
    client
      .from("boletas_honorarios")
      .select("id, computa, tipo_contable, cuenta_er, fecha_emision")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
  ]);

  const docs = [
    ...(sii.data || []).map((f) => normalizarSiiRecibida(f)),
    ...(externas.data || []).map(normalizarExterna),
    ...(honorarios.data || []).filter((b) => b.computa).map(normalizarHonorario),
  ];

  return docs.filter(
    (d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO && derivarClasificacion(d).estado === CLASIFICACION.SIN_CLASIFICAR
  ).length;
}

// Movimientos bancarios sin vincular desde el corte (sin FK emitida/recibida/caja, no ignorados).
export async function getCountMovimientosPendientes(pais) {
  const { count } = await client
    .from("movimientos_bancarios")
    .select("id", { count: "exact", head: true })
    .eq("pais", pais)
    .gte("fecha", FECHA_CORTE)
    .is("factura_sii_emitida_id", null)
    .is("factura_sii_recibida_id", null)
    .is("movimiento_caja_id", null)
    .neq("estado", "ignorado");
  return count || 0;
}

// Movimientos bancarios vinculados a pagos de empleados (se restan del badge de Caja).
export async function getCountPagosEmpleadoMovs(pais) {
  const { count } = await client
    .from("movimientos_bancarios")
    .select("id, pagos_empleados!inner(id)", { count: "exact", head: true })
    .eq("pais", pais);
  return count || 0;
}

// Badge compuesto "Caja": movimientos pendientes + recibidas pendientes − pagos a empleado.
export async function getCountCajaPendientes(pais) {
  const [movs, recibidas, pagos] = await Promise.all([
    getCountMovimientosPendientes(pais),
    getCountRecibidasPendientes(pais),
    getCountPagosEmpleadoMovs(pais),
  ]);
  return movs + recibidas - pagos;
}
